// src/Pages/CouponAssignment/index.jsx

import React, { useState, useEffect } from "react";
import {
    Flex,
    Select,
    Tag,
    Tabs,
    Button,
    Space,
    Tooltip,
    Modal,
    DatePicker,
    Typography,
    Descriptions,
} from "antd";
import {
    PlusOutlined,
    AppleFilled,
    AndroidFilled,
    MailOutlined,
    EyeOutlined,
    DeleteOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import DebouncedInput from "../../Component/DebouncedInput";
import CustomTable from "../../Component/Table";
import { FilterContainer } from "../../Styles/filterStyle";
import {
    getAllCoupons,
    getCouponAssignments,
    sendCouponAssignmentEmail,
    deleteCouponAssignment,
    getPlanAndOfferConfigAccess,
    downloadCouponAssignments,
} from "../../Services/coupon";
import { formatDate } from "../../utils/date";
import BulkAssignCouponModal from "./BulkAssignCouponModal";
import OfferCodesTab from "./OfferCodesTab";

const { Text } = Typography;

// Platform is a display-only snapshot - it never gates who may receive a code.
const platformIcon = (platform) => {
    if (platform === "ios") {
        return (
            <Tooltip title="iOS">
                <AppleFilled style={{ fontSize: 16 }} />
            </Tooltip>
        );
    }
    if (platform === "android") {
        return (
            <Tooltip title="Android">
                <AndroidFilled style={{ fontSize: 16, color: "#3ddc84" }} />
            </Tooltip>
        );
    }
    return <Text type="secondary">-</Text>;
};

const typeLabel = (t) => (t === "ios_store_code" ? "iOS Store Code" : "Android Coupon");

const statusTag = (status) => {
    const color = status === "redeemed" ? "green" : status === "expired" ? "red" : "blue";
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "-";
    return <Tag color={color}>{label}</Tag>;
};

const CouponAssignment = () => {
    const [activeTab, setActiveTab] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [couponFilter, setCouponFilter] = useState(null);
    const [platformFilter, setPlatformFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isBulkModal, setIsBulkModal] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    // assignment_id currently being emailed, so only that row spins.
    const [emailingId, setEmailingId] = useState(null);

    const skip = (currentPage - 1) * pageSize;

    // Offer Codes is restricted to the admins listed in the
    // plan_and_offer_config_allowed_emails config value. Resolved server-side
    // from the caller's token; undefined while loading, so the tab only appears
    // once access is actually confirmed.
    const { data: canSeeOfferCodes } = useQuery({
        queryKey: ["plan-and-offer-config-access"],
        queryFn: getPlanAndOfferConfigAccess,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Tabs are preset filters over the SAME query - there is no second data
    // source. A tab pins ONE dimension (platform or status) and disables that
    // dropdown; the other dropdowns still apply on top, so "iOS Store Codes"
    // plus status "Redeemed" still works.
    const tabPlatform = ["android", "ios"].includes(activeTab) ? activeTab : null;
    const tabStatus = ["assigned", "redeemed", "expired"].includes(activeTab) ? activeTab : null;
    // The one tab that is NOT a filter over the assignment list: it shows the
    // pool the iOS assignments draw from, so it replaces the body entirely.
    // Gated on access so a disallowed admin falls back to the normal list
    // instead of a permanently disabled query.
    const isOfferCodesTab = activeTab === "offer_codes" && !!canSeeOfferCodes;

    const query = {
        skip,
        limit: pageSize,
        search: searchTerm,
        couponId: couponFilter,
        // The TAB wins over the dropdown, not the other way round: a tab is the
        // more visible commitment, so letting a stale dropdown value override it
        // would show e.g. Assigned rows while the Redeemed tab reads as selected.
        // The dropdown is disabled for whichever dimension a tab already pins.
        platform: tabPlatform || platformFilter,
        status: tabStatus || statusFilter,
        startDate: dateRange?.[0]?.startOf("day").toISOString(),
        endDate: dateRange?.[1]?.endOf("day").toISOString(),
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeTab, couponFilter, platformFilter, statusFilter, dateRange]);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ["coupon-assignments", query],
        queryFn: () => getCouponAssignments(query),
        enabled: !isOfferCodesTab,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Same filters the table is showing (tab included), minus pagination.
    const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
        mutationFn: async () => {
            const { skip: _skip, limit: _limit, ...exportFilters } = query;
            return await downloadCouponAssignments(exportFilters);
        },
    });

    // Populates the coupon dropdown. Includes discount coupons (plan_id NULL).
    const { data: couponData } = useQuery({
        queryKey: ["coupons-for-assignment-filter"],
        queryFn: () => getAllCoupons({ limit: 100 }),
        refetchOnWindowFocus: false,
        retry: false,
    });
    const coupons = couponData?.coupons || [];

    const handleSendEmail = async (row) => {
        setEmailingId(row.assignment_id);
        try {
            await sendCouponAssignmentEmail({ assignment_id: row.assignment_id });
            refetch();
        } finally {
            setEmailingId(null);
        }
    };

    const handleDelete = (row) => {
        Modal.confirm({
            title: "Remove this assignment?",
            content:
                row.platform === "ios"
                    ? "The store code reserved for this user will return to the pool."
                    : "The user will no longer be able to redeem this coupon.",
            okText: "Remove",
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await deleteCouponAssignment(row.assignment_id);
                if (res) refetch();
            },
        });
    };

    // A tab pins one dimension and disables that dropdown, so any value left in
    // the disabled dropdown must be dropped on the way out - otherwise it would
    // silently re-apply itself on "All" and the list would not match the tab.
    const handleTabChange = (key) => {
        if (["android", "ios"].includes(activeTab)) setPlatformFilter(null);
        if (["assigned", "redeemed", "expired"].includes(activeTab)) setStatusFilter(null);
        setActiveTab(key);
    };

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const columns = [
        {
            title: "Code / Coupon",
            dataIndex: "code",
            key: "code",
            render: (t) => <Text strong>{t || "-"}</Text>,
        },
        { title: "Type", dataIndex: "type", key: "type", render: (t) => typeLabel(t) },
        {
            title: "Platform",
            dataIndex: "platform",
            key: "platform",
            align: "center",
            render: (p) => platformIcon(p),
        },
        { title: "Assigned To", dataIndex: "user_name", key: "user_name", render: (t) => t || "-" },
        { title: "Email", dataIndex: "email", key: "email", render: (t) => t || "-" },
        {
            title: "Assigned Date",
            dataIndex: "assigned_at",
            key: "assigned_at",
            render: (t) => (t ? formatDate(t) : "-"),
        },
        { title: "Status", dataIndex: "status", key: "status", render: (s) => statusTag(s) },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => {
                const isRedeemed = row.status === "redeemed";
                return (
                    <Space size="small">
                        <Tooltip title="View details">
                            <Button
                                type="text"
                                shape="circle"
                                icon={<EyeOutlined />}
                                onClick={() => setViewRow(row)}
                            />
                        </Tooltip>
                        {/* Email is a separate step from assigning, so a failed
                            send can be retried without a duplicate assignment. */}
                        <Tooltip
                            title={
                                isRedeemed
                                    ? "Already redeemed - cannot email"
                                    : row.email_sent
                                    ? "Resend email"
                                    : "Send email"
                            }
                        >
                            <Button
                                type="text"
                                shape="circle"
                                loading={emailingId === row.assignment_id}
                                disabled={isRedeemed}
                                icon={
                                    <MailOutlined
                                        style={row.email_sent ? { color: "#52c41a" } : undefined}
                                    />
                                }
                                onClick={() => handleSendEmail(row)}
                            />
                        </Tooltip>
                        {/* <Tooltip title={isRedeemed ? "Redeemed - cannot remove" : "Remove assignment"}>
                            <Button
                                type="text"
                                shape="circle"
                                danger
                                disabled={isRedeemed}
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(row)}
                            />
                        </Tooltip> */}
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            {/* The shared DebouncedInput formats its width prop as `${width}px`,
                so "100%" produces invalid CSS. We stretch the WRAPPER with
                flex:1 instead and lift the input's own max-width here. */}
            <style>{`
                .coupon-search-grow .ant-input-affix-wrapper,
                .coupon-search-grow .ant-input {
                    max-width: 100% !important;
                    width: 100%;
                }
            `}</style>

            <Flex justify="space-between" align="center">
                <h3 style={{ margin: 0 }}>Coupons / Promo Codes Management</h3>
                {!isOfferCodesTab && (
                    <Flex gap="middle">
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsBulkModal(true)}>
                            Assign Coupon
                        </Button>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={triggerDownload} loading={isDownloading}>
                            Export
                        </Button>
                    </Flex>
                )}
            </Flex>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                style={{ marginTop: 4, marginBottom: 0 }}
                tabBarStyle={{ marginBottom: 12 }}
                items={[
                    { key: "all", label: "All" },
                    { key: "android", label: "Android Coupons" },
                    { key: "ios", label: "iOS Store Codes" },
                    { key: "assigned", label: "Assigned" },
                    { key: "redeemed", label: "Redeemed" },
                    { key: "expired", label: "Expired" },
                    ...(canSeeOfferCodes ? [{ key: "offer_codes", label: "Offer Codes" }] : []),
                ]}
            />

            {isOfferCodesTab ? (
                <OfferCodesTab />
            ) : (
                <>
                {/* One line, must not wrap - the search box absorbs the leftover
                    width via flex:1 so the row fills the page. */}
                <FilterContainer style={{ flexWrap: "nowrap", marginBottom: 10 }}>
                    <Select
                        allowClear
                        placeholder="All Types"
                        style={{ width: 140, flex: "0 0 140px" }}
                        disabled={!!tabPlatform}
                        value={tabPlatform || platformFilter}
                        onChange={setPlatformFilter}
                        options={[
                            { label: "iOS Store Code", value: "ios" },
                            { label: "Android Coupon", value: "android" },
                        ]}
                    />
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="All Coupons"
                        style={{ width: 150, flex: "0 0 150px" }}
                        value={couponFilter}
                        onChange={setCouponFilter}
                        options={coupons.map((c) => ({ label: c.code, value: c.id }))}
                    />
                    <Select
                        allowClear
                        placeholder="All Status"
                        style={{ width: 130, flex: "0 0 130px" }}
                        disabled={!!tabStatus}
                        value={tabStatus || statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { label: "Assigned", value: "assigned" },
                            { label: "Redeemed", value: "redeemed" },
                            { label: "Expired", value: "expired" },
                        ]}
                    />
                    <DatePicker.RangePicker
                        style={{ width: 230, flex: "0 0 230px" }}
                        value={dateRange}
                        onChange={setDateRange}
                    />
                    <div className="coupon-search-grow" style={{ flex: 1, minWidth: 180 }}>
                        <DebouncedInput
                            placeholder="Search by code, user name or email..."
                            value={searchTerm}
                            onChange={setSearchTerm}
                            delay={1000}
                        />
                    </div>
                </FilterContainer>

                <CustomTable
                    columns={columns}
                    data={data?.assignments || []}
                    loading={isLoading}
                    rowKey={(r) => r.assignment_id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: data?.total_count || 0,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        position: ["bottomRight"],
                    }}
                    onTableChange={handleTableChange}
                    maxHeight="calc(100vh - 300px)"
                />

                <Modal
                    title="Assignment Details"
                    open={!!viewRow}
                    onCancel={() => setViewRow(null)}
                    footer={null}
                    width={620}
                >
                    {viewRow && (
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Code / Coupon">{viewRow.code || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Coupon Code">{viewRow.coupon_code || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Type">{typeLabel(viewRow.type)}</Descriptions.Item>
                            <Descriptions.Item label="Platform">{platformIcon(viewRow.platform)}</Descriptions.Item>
                            <Descriptions.Item label="Plan">{viewRow.plan_name || "All Plans"}</Descriptions.Item>
                            <Descriptions.Item label="Assigned To">{viewRow.user_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Email">{viewRow.email || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Assigned Date">
                                {viewRow.assigned_at ? formatDate(viewRow.assigned_at) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email Sent">
                                <Tag color={viewRow.email_sent ? "green" : "default"}>
                                    {viewRow.email_sent ? "Yes" : "No"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Redeemed By">{viewRow.redeemed_by || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Redeemed Date">
                                {viewRow.redeemed_at ? formatDate(viewRow.redeemed_at) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Expires At">
                                {viewRow.expires_at ? formatDate(viewRow.expires_at) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">{statusTag(viewRow.status)}</Descriptions.Item>
                            {/* iOS only - the pool a store code came from and
                                what state that code is in. Null for Android,
                                which consumes no code. */}
                            {viewRow.ios_offer_code && (
                                <Descriptions.Item label="Store Code Status">
                                    <Tag
                                        color={
                                            viewRow.ios_offer_code_status === "redeemed"
                                                ? "green"
                                                : viewRow.ios_offer_code_status === "expired"
                                                ? "red"
                                                : "blue"
                                        }
                                    >
                                        {viewRow.ios_offer_code_status || "-"}
                                    </Tag>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    )}
                </Modal>
                </>
            )}

            <BulkAssignCouponModal
                isModalVisible={isBulkModal}
                handleCancel={() => {
                    setIsBulkModal(false);
                    refetch();
                }}
            />
        </div>
    );
};

export default CouponAssignment;
